/**
 * OIMS v2 - Backup tab (Command Bridge)
 * Two-phase validate + upload with SSE streaming and workflow indicator
 */

import { escapeHtml, showLoading, hideLoading, clearLog, appendLog } from './utils.js';

let dbName, sftpPath, btnBackup, backupResult;
let backupSSE = null;

export function initBackup(state) {
  dbName = document.getElementById("dbName");
  sftpPath = document.getElementById("sftpPath");
  btnBackup = document.getElementById("btnBackup");
  backupResult = document.getElementById("backupResult");

  // Set dynamic date placeholder
  if (dbName) {
    const d = new Date();
    const pad = n => String(n).padStart(2, "0");
    dbName.placeholder = `e.g. org_core_${d.getFullYear()}_${pad(d.getMonth()+1)}_${pad(d.getDate())}`;
  }

  btnBackup && btnBackup.addEventListener("click", () => confirmAndBackup(state));

  // Input change -> update briefing
  if (dbName) dbName.addEventListener("input", updateBriefing);
  if (sftpPath) sftpPath.addEventListener("input", updateBriefing);

  // Terminal toolbar
  const btnClear = document.getElementById("btnClearBackupLog");
  const btnCopy = document.getElementById("btnCopyBackupLog");
  if (btnClear) btnClear.addEventListener("click", () => clearLog(backupResult));
  if (btnCopy) btnCopy.addEventListener("click", () => copyLogToClipboard());

  // Initial briefing
  updateBriefing();
}

function setBackupStatus(text, color = "secondary") {
  const badge = document.getElementById("backupStatusBadge");
  if (!badge) return;
  badge.textContent = text;
  const variantMap = { secondary: "idle", info: "running", success: "success", danger: "error", primary: "running", warning: "running" };
  const variant = variantMap[color] || "idle";
  badge.className = `status-badge ${variant}`;
}

function updateBriefing() {
  const briefingText = document.getElementById("backupBriefingText");
  if (!briefingText) return;

  const filename = dbName?.value?.trim();
  const folder = sftpPath?.value?.trim();

  if (filename && folder) {
    briefingText.innerHTML = `<span class="briefing-ready"><i class="bi bi-check-circle me-1"></i>Ready: <strong>${escapeHtml(filename)}</strong> → <strong>/ofoq/${escapeHtml(folder)}</strong></span>`;
  } else if (filename) {
    briefingText.textContent = `File: ${filename} — enter SFTP folder path`;
  } else {
    briefingText.textContent = "Enter backup filename and SFTP path to begin upload process";
  }

  // Update launch button subtitle
  const launchSmall = btnBackup?.querySelector(".launch-text small");
  if (launchSmall) {
    if (filename && folder) {
      launchSmall.textContent = `${filename} → /ofoq/${folder}`;
    } else {
      launchSmall.textContent = "Validate and upload to production";
    }
  }
}

function copyLogToClipboard() {
  if (!backupResult) return;
  const text = backupResult.innerText || backupResult.textContent || "";
  if (!text.trim()) {
    window.toast?.info("Nothing to copy.");
    return;
  }
  navigator.clipboard.writeText(text).then(() => {
    window.toast?.success("Output copied to clipboard.");
  }).catch(() => {
    window.toast?.error("Failed to copy.");
  });
}

function showBackupMetadata(filename, folder) {
  const meta = document.getElementById("backupMetadata");
  const metaFile = document.getElementById("metaBackupFile");
  const metaPath = document.getElementById("metaBackupPath");
  const metaTime = document.getElementById("metaBackupTime");
  if (meta) {
    meta.style.display = "";
    if (metaFile) metaFile.textContent = filename;
    if (metaPath) metaPath.textContent = `/ofoq/${folder}`;
    if (metaTime) metaTime.textContent = new Date().toLocaleTimeString();
  }
}

// Workflow step states: pending, active, completed, error
function setWorkflowStep(stepId, stepState) {
  const step = document.getElementById(stepId);
  if (!step) return;
  step.classList.remove("active", "completed", "error");
  if (stepState !== "pending") step.classList.add(stepState);
}

function resetWorkflow() {
  setWorkflowStep("workflowValidate", "pending");
  setWorkflowStep("workflowUpload", "pending");
  setWorkflowStep("workflowComplete", "pending");
}

function setLaunchState(executing, label) {
  if (!btnBackup) return;
  if (executing) {
    btnBackup.disabled = true;
    btnBackup.classList.add("executing");
    const icon = btnBackup.querySelector(".launch-icon i");
    if (icon) icon.className = "bi bi-arrow-repeat";
    const strong = btnBackup.querySelector(".launch-text strong");
    if (strong) strong.textContent = label || "Processing...";
  } else {
    btnBackup.disabled = false;
    btnBackup.classList.remove("executing");
    const icon = btnBackup.querySelector(".launch-icon i");
    if (icon) icon.className = "bi bi-cloud-arrow-up-fill";
    const strong = btnBackup.querySelector(".launch-text strong");
    if (strong) strong.textContent = "Deploy Backup";
    updateBriefing();
  }
}

function stopBackupStream() {
  if (backupSSE) {
    backupSSE.close();
    backupSSE = null;
  }
}

function startBackupStream(state, instance_id, command_id) {
  stopBackupStream();

  const url = new URL("/actions/stream", window.location.origin);
  url.searchParams.set("instance_id", instance_id);
  url.searchParams.set("command_id", command_id);
  url.searchParams.set("access_token", state.accessToken);
  url.searchParams.set("action_id", "backup_sql");

  backupSSE = new EventSource(url.toString());

  backupSSE.addEventListener("message", (ev) => {
    try {
      const payload = JSON.parse(ev.data);
      if (payload && payload.chunk) {
        appendLog(backupResult, payload.chunk);
      } else {
        appendLog(backupResult, ev.data);
      }
    } catch {
      appendLog(backupResult, ev.data);
    }
  });

  backupSSE.addEventListener("done", () => {
    appendLog(backupResult, "\n────────────────────────────────────");
    appendLog(backupResult, "Upload process finished.");
    setBackupStatus("Complete", "success");
    setWorkflowStep("workflowUpload", "completed");
    setWorkflowStep("workflowComplete", "completed");
    window.toast.success("Backup upload process completed");
    setLaunchState(false);
    setTimeout(stopBackupStream, 120);
  });

  backupSSE.addEventListener("error", () => {
    if (!backupSSE || backupSSE.readyState === EventSource.CLOSED) {
      appendLog(backupResult, "\n--- Stream closed ---");
    } else {
      appendLog(backupResult, "\n--- Connection lost. The upload may still be running on the server. ---");
      appendLog(backupResult, "Check the Logs tab for the final result.");
      window.toast.warning("Stream disconnected. Upload may still be running.");
    }
    setBackupStatus("Disconnected", "warning");
    setWorkflowStep("workflowUpload", "error");
    setLaunchState(false);
    stopBackupStream();
  });
}

function confirmAndBackup(state) {
  if (!state.accessToken) {
    window.toast.warning("Please sign in to run backups.");
    return;
  }
  const filename = dbName.value.trim();
  const folder = sftpPath.value.trim();
  if (!filename || !folder) {
    window.toast.warning("Please enter backup filename and SFTP folder.");
    return;
  }

  document.getElementById("confirmActionName").textContent = "Deploy Backup";
  document.getElementById("confirmInstanceName").textContent = `${filename} → /ofoq/${folder}`;

  const approvalHint = document.getElementById("confirmApprovalHint");
  if (approvalHint) approvalHint.style.display = 'none';

  const modalEl = document.getElementById("confirmActionModal");
  const modal = new bootstrap.Modal(modalEl);
  modal.show();

  const btnConfirm = document.getElementById("btnConfirmExecute");
  const newBtn = btnConfirm.cloneNode(true);
  btnConfirm.parentNode.replaceChild(newBtn, btnConfirm);
  newBtn.addEventListener("click", () => {
    modal.hide();
    doBackup(state);
  });
}

async function doBackup(state) {
  if (!state.accessToken) {
    window.toast.warning("Please sign in to run backups.");
    return;
  }
  const filename = dbName.value.trim();
  const folder = sftpPath.value.trim();
  if (!filename || !folder) {
    window.toast.warning("Please enter backup filename and SFTP folder.");
    return;
  }

  // Reset and start workflow
  resetWorkflow();
  setWorkflowStep("workflowValidate", "active");
  setLaunchState(true, "Validating...");
  setBackupStatus("Validating...", "info");
  showBackupMetadata(filename, folder);
  showLoading("Validating backup file and SFTP connection...");
  clearLog(backupResult);
  appendLog(backupResult, "Validating...");

  const backupSafetyTimer = setTimeout(() => {
    setLaunchState(false);
    setBackupStatus("Timeout", "danger");
    setWorkflowStep("workflowValidate", "error");
    window.toast.warning("Backup operation timed out. Please try again.");
  }, 300000);

  try {
    const { data: validation } = await axios.post(
      `/backup/validate?backup_filename=${encodeURIComponent(filename)}&sftp_folder=${encodeURIComponent(folder)}`
    );

    hideLoading();

    if (validation.messages) {
      for (const msg of validation.messages) {
        const icon = msg.includes("not found") ? "  \u2717 " : "  \u2713 ";
        appendLog(backupResult, icon + msg);
      }
    }
    if (validation.warnings) {
      for (const msg of validation.warnings) {
        appendLog(backupResult, "  \u26A0 " + msg);
      }
    }
    appendLog(backupResult, "");

    if (!validation.valid) {
      clearTimeout(backupSafetyTimer);
      const errorMsg = validation.error || validation.messages?.join(", ") || "Validation failed";
      appendLog(backupResult, "VALIDATION FAILED - Upload cancelled.");
      setBackupStatus("Failed", "danger");
      setWorkflowStep("workflowValidate", "error");
      window.toast.error(errorMsg);
      setLaunchState(false);
      return;
    }

    // Validation passed -> move to upload step
    setWorkflowStep("workflowValidate", "completed");
    setWorkflowStep("workflowUpload", "active");

    const fileSize = validation.file_info?.size || "unknown size";
    const fileName = validation.file_info?.filename || filename;

    window.toast.success("Validation passed. Starting upload...");
    appendLog(backupResult, `Starting archive & upload of ${fileName} (${fileSize})...`);
    appendLog(backupResult, "This may take a while for large files.\n");

    setLaunchState(true, "Uploading...");
    setBackupStatus("Uploading...", "primary");

    const { data } = await axios.post(
      `/backup/?backup_filename=${encodeURIComponent(filename)}&sftp_folder=${encodeURIComponent(folder)}`
    );

    if (data.status === "sent") {
      clearTimeout(backupSafetyTimer);
      startBackupStream(state, data.instance_id, data.command_id);
    } else {
      clearTimeout(backupSafetyTimer);
      appendLog(backupResult, "Unexpected response: " + JSON.stringify(data, null, 2));
      setBackupStatus("Error", "danger");
      setWorkflowStep("workflowUpload", "error");
      setLaunchState(false);
    }
  } catch (e) {
    clearTimeout(backupSafetyTimer);
    hideLoading();
    appendLog(backupResult, "ERROR: " + (e.response?.data?.detail || e.message));
    setBackupStatus("Error", "danger");
    window.toast.error("Backup failed: " + (e.response?.data?.detail || e.message));
    setLaunchState(false);
  }
}
